// Jobs — the deployable-unit catalog (from getJobs), filterable by app, type,
// and text. Rows link to routed job detail pages instead of a modal.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  InputAdornment,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Construction as JobsIcon, Search as SearchIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useDeploymentData } from '../hooks/useDeploymentData';
import { useV2Filters } from '../hooks/useV2Filters';
import { getColors, rounded } from '../theme/tokens';
import PageHeader from '../components/PageHeader';
import MultiSelectFilter from '../components/MultiSelectFilter';
import TypeBadge from '../components/TypeBadge';
import EmptyState from '../components/EmptyState';
import { jobPath } from '../lib/jobPath';
import InfoPopover from '../components/InfoPopover';

// Chip tints — App matches the Services directory; Type matches the Activity
// feed's "Type" category color.
const APP_HUE = '#0075de';
const TYPE_HUE = '#2a9d99';
const ACCOUNT_HUE = '#8155b8';

const JobsPage: React.FC = () => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { jobs, jobsLoading, jobsLoaded, fetchJobs, wsStatus } = useDeploymentData();
  const { apps: selectedApps, types: selectedTypes, setApps, setTypes } = useV2Filters();
  const [query, setQuery] = useState('');

  // Account filter (multi-select, URL-synced via ?accounts=). Jobs-specific, so
  // managed locally rather than through the shared useV2Filters hook.
  const accountsParam = searchParams.get('accounts');
  const selectedAccounts = useMemo(
    () => (accountsParam ? accountsParam.split(',').filter(Boolean) : []),
    [accountsParam]
  );
  const setAccounts = (next: string[]) => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (next.length > 0) p.set('accounts', next.join(','));
      else p.delete('accounts');
      return p;
    });
  };

  // Prod filter: 'all' (default) | 'prod' | 'nonprod', URL-synced via ?prod=.
  const prodFilter = (searchParams.get('prod') as 'prod' | 'nonprod' | null) || 'all';
  const setProd = (value: 'all' | 'prod' | 'nonprod') => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value === 'all') next.delete('prod');
      else next.set('prod', value);
      return next;
    });
  };

  // Legacy deep link: /jobs?job=<job_id> → routed job page.
  const legacyJob = searchParams.get('job');

  useEffect(() => {
    if (wsStatus === 'connected' && !jobsLoaded && !jobsLoading) fetchJobs();
  }, [wsStatus, jobsLoaded, jobsLoading, fetchJobs]);

  // Back-compat: older links use ?app=<app> (single). Migrate to the multi-select ?apps=.
  useEffect(() => {
    const legacyApp = searchParams.get('app');
    if (!legacyApp) return;
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete('app');
        const existing = next.get('apps');
        const merged = Array.from(
          new Set([...(existing ? existing.split(',').filter(Boolean) : []), legacyApp])
        ).join(',');
        next.set('apps', merged);
        return next;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (legacyJob && jobs.length > 0) {
      const match = jobs.find(j => j.job_id === legacyJob);
      if (match && match.app_name && match.job_id) {
        navigate(jobPath(match.app_name, match.job_id, match.type), { replace: true });
      }
    }
  }, [legacyJob, jobs, navigate]);

  const types = useMemo(
    () => Array.from(new Set(jobs.map(j => j.type).filter(Boolean))).sort(),
    [jobs]
  );
  const apps = useMemo(
    () => Array.from(new Set(jobs.map(j => j.app_name).filter(Boolean))).sort(),
    [jobs]
  );
  const accounts = useMemo(
    () => Array.from(new Set(jobs.flatMap(j => j.deployable_accounts || []).filter(Boolean))).sort(),
    [jobs]
  );

  const visible = useMemo(() => {
    const q = query.toLowerCase().trim();
    const appSet = new Set(selectedApps);
    const typeSet = new Set(selectedTypes);
    const accountSet = new Set(selectedAccounts);
    // Real catalog entries can miss app_name/job_id — guard every access
    // (the legacy Jobs view null-guarded these for the same reason).
    return jobs
      .filter(j => {
        if (appSet.size > 0 && !(j.app_name && appSet.has(j.app_name))) return false;
        if (typeSet.size > 0 && !(j.type && typeSet.has(j.type))) return false;
        if (accountSet.size > 0 && !(j.deployable_accounts || []).some(a => accountSet.has(a)))
          return false;
        if (prodFilter === 'prod' && !j.is_prod) return false;
        if (prodFilter === 'nonprod' && j.is_prod) return false;
        if (
          q &&
          !(j.job_id || '').toLowerCase().includes(q) &&
          !(j.app_name || '').toLowerCase().includes(q)
        )
          return false;
        return true;
      })
      .sort(
        (a, b) =>
          (a.app_name || '').localeCompare(b.app_name || '') ||
          (a.job_id || '').localeCompare(b.job_id || '')
      );
  }, [jobs, selectedApps, selectedTypes, selectedAccounts, query, prodFilter]);

  return (
    <Box>
      <PageHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Jobs
            <InfoPopover entry="jobId" size={16} />
          </Box>
        }
        subtitle="The catalog of deployable units and their definitions"
      />

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        {/* App filter (multi-select, URL-synced via ?apps=) */}
        <MultiSelectFilter
          options={apps}
          value={selectedApps}
          onChange={setApps}
          hue={APP_HUE}
          placeholder="Filter by app…"
        />
        <TextField
          size="small"
          placeholder="Filter jobs…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 17, color: c.inkFaint }} />
              </InputAdornment>
            ),
          }}
          sx={{ width: 240 }}
        />
        {/* Type filter (multi-select, URL-synced via ?types=) */}
        <MultiSelectFilter
          options={types}
          value={selectedTypes}
          onChange={setTypes}
          hue={TYPE_HUE}
          placeholder="Filter by type…"
        />
        {/* Account filter (multi-select, URL-synced via ?accounts=) */}
        <MultiSelectFilter
          options={accounts}
          value={selectedAccounts}
          onChange={setAccounts}
          hue={ACCOUNT_HUE}
          placeholder="Filter by account…"
        />
        {/* Prod filter (URL-synced via ?prod=) */}
        <ToggleButtonGroup
          size="small"
          exclusive
          value={prodFilter}
          onChange={(_, val) => {
            if (val) setProd(val);
          }}
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontSize: '0.8rem',
              fontWeight: 600,
              py: 0.5,
              px: 1.25,
              color: c.inkMuted,
              borderColor: c.hairline,
              '&.Mui-selected': {
                color: c.ink,
                backgroundColor: c.hoverWash,
                '&:hover': { backgroundColor: c.hoverWash },
              },
            },
          }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="prod">Prod</ToggleButton>
          <ToggleButton value="nonprod">Non-prod</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" sx={{ color: c.inkMuted }}>
          {visible.length} of {jobs.length} jobs
        </Typography>
      </Box>

      {jobsLoading && jobs.length === 0 ? (
        <Box>
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={44} sx={{ borderRadius: 1, mb: 1 }} />
          ))}
        </Box>
      ) : visible.length === 0 ? (
        <EmptyState
          icon={<JobsIcon />}
          title={jobs.length === 0 ? 'No jobs loaded' : 'No jobs match'}
          body={
            jobs.length === 0
              ? 'The job catalog will load once the connection is established.'
              : 'Adjust the filters to see more jobs.'
          }
        />
      ) : (
        <Paper
          variant="outlined"
          sx={{ borderRadius: `${rounded.lg}px`, borderColor: c.hairline, overflow: 'hidden' }}
        >
          <TableContainer sx={{ maxHeight: 'calc(100vh - 280px)' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>App</TableCell>
                  <TableCell>Job ID</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Prod</TableCell>
                  <TableCell>Accounts</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((job, index) => {
                  const navigable = Boolean(job.app_name && job.job_id);
                  return (
                  <TableRow
                    key={`${job.app_name || ''}:${job.job_id || ''}:${index}`}
                    hover
                    onClick={
                      navigable
                        ? () =>
                            navigate(
                              jobPath(job.app_name, job.job_id, job.type)
                            )
                        : undefined
                    }
                    onKeyDown={
                      navigable
                        ? e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              navigate(
                                jobPath(job.app_name, job.job_id, job.type)
                              );
                            }
                          }
                        : undefined
                    }
                    tabIndex={navigable ? 0 : -1}
                    role={navigable ? 'button' : undefined}
                    sx={{ cursor: navigable ? 'pointer' : 'default' }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {job.app_name || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{job.job_id || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <TypeBadge type={job.type} />
                    </TableCell>
                    <TableCell>
                      {job.is_prod ? (
                        <Chip
                          label="prod"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            backgroundColor: '#d93a3a14',
                            color: '#d93a3a',
                            border: '1px solid #d93a3a44',
                          }}
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: c.inkMuted }} noWrap>
                        {(job.deployable_accounts || []).join(', ')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default JobsPage;
